import { useState, useEffect, useCallback } from "react";
import { SleepEntry, FeedingEntry } from "@/lib/babyData";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export function useBabyData() {
  const { user } = useAuth();
  const [sleepEntries, setSleepEntries] = useState<SleepEntry[]>([]);
  const [feedingEntries, setFeedingEntries] = useState<FeedingEntry[]>([]);

  const fetchData = useCallback(async () => {
    if (!user) {
      setSleepEntries([]);
      setFeedingEntries([]);
      return;
    }

    const [sleepRes, feedingRes] = await Promise.all([
      supabase
        .from("sleep_entries")
        .select("*")
        .order("created_at", { ascending: false }),
      supabase
        .from("feeding_entries")
        .select("*")
        .order("created_at", { ascending: false }),
    ]);

    if (sleepRes.data) {
      setSleepEntries(
        sleepRes.data.map((r) => ({
          id: r.id,
          date: r.date,
          startTime: r.start_time,
          endTime: r.end_time,
          duration: r.duration,
          type: r.type as "nap" | "night",
          notes: r.notes || "",
        }))
      );
    }

    if (feedingRes.data) {
      setFeedingEntries(
        feedingRes.data.map((r) => ({
          id: r.id,
          date: r.date,
          time: r.time,
          type: r.type as "breast" | "bottle" | "solid",
          amount: r.amount || "",
          notes: r.notes || "",
        }))
      );
    }
  }, [user]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const addSleepEntry = async (entry: Omit<SleepEntry, "id">) => {
    if (!user) return;
    const { data, error } = await supabase.from("sleep_entries").insert({
      user_id: user.id,
      date: entry.date,
      start_time: entry.startTime,
      end_time: entry.endTime,
      duration: entry.duration,
      type: entry.type,
      notes: entry.notes,
    }).select().single();

    if (!error && data) {
      setSleepEntries((prev) => [
        {
          id: data.id,
          date: data.date,
          startTime: data.start_time,
          endTime: data.end_time,
          duration: data.duration,
          type: data.type as "nap" | "night",
          notes: data.notes || "",
        },
        ...prev,
      ]);
    }
  };

  const addFeedingEntry = async (entry: Omit<FeedingEntry, "id">) => {
    if (!user) return;
    const { data, error } = await supabase.from("feeding_entries").insert({
      user_id: user.id,
      date: entry.date,
      time: entry.time,
      type: entry.type,
      amount: entry.amount,
      notes: entry.notes,
    }).select().single();

    if (!error && data) {
      setFeedingEntries((prev) => [
        {
          id: data.id,
          date: data.date,
          time: data.time,
          type: data.type as "breast" | "bottle" | "solid",
          amount: data.amount || "",
          notes: data.notes || "",
        },
        ...prev,
      ]);
    }
  };

  const deleteSleepEntry = async (id: string) => {
    await supabase.from("sleep_entries").delete().eq("id", id);
    setSleepEntries((prev) => prev.filter((e) => e.id !== id));
  };

  const deleteFeedingEntry = async (id: string) => {
    await supabase.from("feeding_entries").delete().eq("id", id);
    setFeedingEntries((prev) => prev.filter((e) => e.id !== id));
  };

  return { sleepEntries, feedingEntries, addSleepEntry, addFeedingEntry, deleteSleepEntry, deleteFeedingEntry };
}
